import { useContext, useImperativeHandle, useState } from 'react';
import classNames from 'classnames';
import { Microphone } from '@phosphor-icons/react';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import AudioContext from 'src/contexts/AudioContext';
import {
  useThreadRegistry,
  useRightSidePanelState,
  useComparingConversationsParams,
} from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import { SVG_SIZE_M } from 'src/constants';
import AuthContext from 'src/contexts/AuthContext';
import { AppRoutes } from 'src/types';

interface MicrophoneTriggerProps {
  startSpeechRecognizing: (defaultValue?: string) => Promise<void>;
  stopSpeechRecognizing: () => Promise<void>;
}

export const MicrophoneTrigger = ({
  startSpeechRecognizing,
  stopSpeechRecognizing,
}: MicrophoneTriggerProps) => {
  const { isSubmitHappened } = useThreadRegistry();
  const { isImageEditSectionExpanded } = useRightSidePanelState();
  const { isCompareMode } = useComparingConversationsParams();
  const [isProceeding, setProceeding] = useState<boolean>(false);
  const { isGuestAccess } = useContext(AuthContext);
  const { microphoneTriggerRef, recordInProgress } = useContext(AudioContext);
  const { showReplaceTooltip, promptLoading, threadInputBoxValue } = useContext(
    ThreadInputBoxContext,
  );

  const handleToggleAction = async () => {
    if (isGuestAccess) {
      window.location.href = AppRoutes.LOGIN;
      return;
    }

    if (showReplaceTooltip) {
      return;
    }

    setProceeding(true);

    if (recordInProgress) {
      await stopSpeechRecognizing();
    } else {
      await startSpeechRecognizing(threadInputBoxValue);
    }

    setProceeding(false);
  };

  useImperativeHandle(microphoneTriggerRef, () => ({
    stopRecording: stopSpeechRecognizing,
    onToggleAction: handleToggleAction,
  }));

  const dynamicClasses: string = classNames({
    'recording-on': recordInProgress,
    'limbo-on': showReplaceTooltip,
    hidden: isSubmitHappened,
  });

  const isDisabled =
    promptLoading ||
    isImageEditSectionExpanded ||
    isCompareMode ||
    showReplaceTooltip ||
    isProceeding;

  return (
    <Button
      aria-label="Click to the action button"
      type="button"
      className={classNames('nj-microphone-button', dynamicClasses)}
      onClick={handleToggleAction}
      disabled={isDisabled}
      shape="round"
      color="transparent"
      tooltipContent="Use voice mode"
    >
      <Microphone
        data-testid="microphone-icon"
        size={SVG_SIZE_M}
        weight={recordInProgress ? 'fill' : 'regular'}
      />
    </Button>
  );
};
